import React, { useEffect, useState } from 'react';
import {
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Table
} from 'reactstrap';
import CurrencyInput from 'react-currency-input-field';
import cogoToast from 'cogo-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { copInputProps, valorFormat } from '../../../helpers/formatters';
import { recaudoServices } from '../../../services';

// Corrige los valores de un lote ya guardado. Cada línea con un valor distinto se reemplaza en el
// mismo lote; valor 0 la quita. La diferencia del total la registra el procedimiento en tesorería.
const pesos = v => `$ ${valorFormat(Number(v))}`;

function CorregirLoteModal({ open, toggle, data, recaudos }) {
  const [valores, setValores] = useState({});
  const [observacion, setObservacion] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [resultado, setResultado] = useState();

  useEffect(() => {
    if (!open) return;
    setValores(Object.fromEntries((recaudos || []).map(r => [r.recaudo, r.valorRecaudo])));
    setObservacion('');
    setResultado(null);
  }, [open]);

  const totalAnterior = (recaudos || []).reduce((a, r) => a + r.valorRecaudo, 0);
  const totalNuevo = (recaudos || []).reduce((a, r) => a + (valores[r.recaudo] ?? 0), 0);
  const cambios = (recaudos || []).filter(r => valores[r.recaudo] !== r.valorRecaudo);

  const onValor = (recaudo, v) => {
    setValores(prev => ({ ...prev, [recaudo]: v ? Number(v.replaceAll(',', '.')) : 0 }));
  }

  const onGuardar = async () => {
    if (cambios.length === 0) {
      cogoToast.warn("No hay cambios para guardar", { position: 'bottom-left', heading: 'Sin cambios' });
      return;
    }
    if (!observacion.trim()) {
      cogoToast.warn("Indique el motivo de la corrección", { position: 'bottom-left', heading: 'Campos requeridos' });
      return;
    }
    setRequesting(true);
    const response = await recaudoServices.corregirLote({
      lote: data.value,
      observacion: observacion.trim(),
      recaudos: cambios.map(r => ({ recaudo: r.recaudo, valorRecaudo: valores[r.recaudo] })),
    });
    setRequesting(false);
    if (!response || response.error) {
      cogoToast.error(response?.msg || "No se logró corregir el lote",
        { position: 'bottom-left', heading: 'Error' });
      return;
    }
    setResultado(response.objResponse);
  }

  const onCerrar = () => toggle(!!resultado);

  return (
    <Modal size="lg" isOpen={open}>
      <ModalHeader
        close={
          <div className='d-flex justify-content-center'>
            <FontAwesomeIcon onClick={onCerrar} style={{ cursor: 'pointer' }} size='lg' icon="times" />
          </div>}
      >
        <h5>{`Corregir Lote ${data ? data.label : ''}`}</h5>
      </ModalHeader>
      <ModalBody>
        {resultado ? (
          <div className="text-center mt-4">
            <FontAwesomeIcon icon="check" transform="shrink-2" className="text-success mr-1 fs-8" />
            <h6 className="fs-2 m-3">Lote corregido</h6>
            <p className="mb-1">{`Total anterior: ${pesos(resultado.totalAnterior)} — Total nuevo: ${pesos(resultado.totalNuevo)}`}</p>
            {Number(resultado.diferencia) !== 0 && (
              <p className="fs--1 mb-0">
                {`Se registró en tesorería un ${Number(resultado.diferencia) > 0 ? 'ingreso' : 'egreso'} por ${pesos(Math.abs(resultado.diferencia))}.`}
              </p>
            )}
          </div>
        ) : (
          <>
            <p className="fs--1">
              Cambie el valor de las líneas mal digitadas. Con valor 0 la línea se quita del lote.
              La diferencia del total se registra en tesorería.
            </p>
            <Table size="sm" className="fs--1">
              <thead>
                <tr>
                  <th>Nro.</th>
                  <th>Cliente</th>
                  <th>Crédito</th>
                  <th className="text-right">Valor actual</th>
                  <th className="text-right" style={{ width: '180px' }}>Valor corregido</th>
                </tr>
              </thead>
              <tbody>
                {(recaudos || []).map(r => (
                  <tr key={r.recaudo} className={valores[r.recaudo] !== r.valorRecaudo ? 'table-warning' : ''}>
                    <td className="align-middle">{r.recaudo}</td>
                    <td className="align-middle">{r.nombreCliente}</td>
                    <td className="align-middle">{r.credito}</td>
                    <td className="align-middle text-right text-nowrap">{pesos(r.valorRecaudo)}</td>
                    <td>
                      <CurrencyInput
                        className="form-control form-control-sm text-right"
                        allowNegativeValue={false}
                        id={`corregir-${r.recaudo}`}
                        prefix='$'
                        {...copInputProps}
                        defaultValue={r.valorRecaudo}
                        onValueChange={(v) => onValor(r.recaudo, v)}
                        decimalsLimit={2}
                        maxLength={20}
                        placeholder="$ 0" />
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan={3}>Total</th>
                  <th className="text-right text-nowrap">{pesos(totalAnterior)}</th>
                  <th className="text-right text-nowrap">{pesos(totalNuevo)}</th>
                </tr>
              </tfoot>
            </Table>
            <FormGroup>
              <Label for="motivoCorreccion">Motivo de la corrección</Label>
              <Input
                type="textarea"
                id="motivoCorreccion"
                maxLength={300}
                value={observacion}
                onChange={e => setObservacion(e.target.value)} />
            </FormGroup>
          </>
        )}
      </ModalBody>
      <ModalFooter className="no-border">
        <Col className="text-right">
          {resultado ? (
            <Button onClick={onCerrar} color="primary">Aceptar</Button>
          ) : (
            <>
              <Button onClick={onCerrar} className="mr-2" color="falcon-default" disabled={requesting}>
                Cancelar
              </Button>
              <Button onClick={onGuardar} color="primary" disabled={requesting}>
                {requesting ? <Spinner size="sm" /> : 'Guardar corrección'}
              </Button>
            </>
          )}
        </Col>
      </ModalFooter>
    </Modal>
  )
}

export default CorregirLoteModal
